import { useEffect, useCallback, useRef } from 'react';
import { config } from '../config/env';
import { useAuth } from '../hooks/useAuth';

/**
 * GoogleSignInButton Component
 *
 * Renders a Google Sign-In button using Google Identity Services SDK.
 * Handles the OAuth flow and communicates with the backend.
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback on successful authentication
 * @param {Function} props.onError - Callback on authentication failure
 * @param {string} props.buttonText - Button text variant: 'signin_with', 'signup_with', 'continue_with'
 * @param {string} props.buttonId - Unique ID for the button container (default: 'google-signin-button')
 */
function GoogleSignInButton({
  onSuccess,
  onError,
  buttonText = 'continue_with',
  buttonId = 'google-signin-button'
}) {
  const { loginWithGoogle } = useAuth();
  const buttonRef = useRef(null);
  const initializedRef = useRef(false);

  const handleGoogleResponse = useCallback(async (response) => {
    try {
      // Send the ID token to our backend
      const result = await loginWithGoogle(response.credential);

      if (result && result.user) {
        onSuccess?.(result);
      } else {
        onError?.('Google sign-in failed. Please try again.');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      // Provide more detailed error message
      let errorMessage = 'Google sign-in failed. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.status === 0) {
        errorMessage = 'Cannot connect to server. Please check if the backend is running.';
      } else if (error.status === 401) {
        errorMessage = 'Invalid Google token. Please try again.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      onError?.(errorMessage);
    }
  }, [loginWithGoogle, onSuccess, onError]);

  useEffect(() => {
    // Check if Google OAuth is enabled
    if (!config.enableGoogleOAuth) {
      console.warn('Google OAuth is disabled in configuration');
      return;
    }

    // Check if Google Client ID is configured
    if (!config.googleClientId) {
      console.warn('Google Client ID is not configured');
      return;
    }

    // Wait for Google SDK to load
    const initializeGoogle = () => {
      if (!window.google?.accounts?.id) {
        // SDK not loaded yet, retry
        setTimeout(initializeGoogle, 100);
        return;
      }

      // Prevent double initialization
      if (initializedRef.current) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: config.googleClientId,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          prompt_parent_id: buttonId,
          use_fedcm_for_prompt: false
        });

        // Revoke any previous session hint to force account selection
        window.google.accounts.id.disableAutoSelect();

        // Render the button
        if (buttonRef.current) {
          window.google.accounts.id.renderButton(
            buttonRef.current,
            {
              theme: 'outline',
              size: 'large',
              text: buttonText,
              shape: 'rectangular',
              width: 300,
              logo_alignment: 'left'
            }
          );
        }

        initializedRef.current = true;
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
        onError?.('Failed to initialize Google Sign-In');
      }
    };

    initializeGoogle();

    // Cleanup
    return () => {
      initializedRef.current = false;
    };
  }, [handleGoogleResponse, buttonText, onError, buttonId]);

  // Don't render if Google OAuth is disabled or not configured
  if (!config.enableGoogleOAuth || !config.googleClientId) {
    return null;
  }

  return (
    <div
      ref={buttonRef}
      id={buttonId}
      className="google-signin-button-container"
      style={{ display: 'flex', justifyContent: 'center' }}
    />
  );
}

export default GoogleSignInButton;
